import { useState, useEffect } from 'react';
import { useReferenceData, useStatus } from '../components/tab-layout';
import { fetchDirections, upsertDirection, deleteDirection, previewImport, commitImport, upsertTerminal, updateTerminalKeywords } from '../lib/api-client';
import { SqlActionModal } from '../components/sql-action-modal';
import { DirectionStepsModal, type DirectionStep } from '../components/direction-steps-modal';
import type { ImportPreviewResponse, ImportCommitResponse } from '../types';

/* Shared styles */
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors';
const btnPrimary = 'px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors cursor-pointer disabled:opacity-50';
const btnSecondary = 'px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors cursor-pointer';
const btnSuccess = 'px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors cursor-pointer disabled:opacity-50';
const btnDanger = 'text-red-500 hover:text-red-700 text-xs font-medium transition-colors cursor-pointer';
const cardCls = 'bg-white rounded-lg shadow-sm border border-gray-200';

/* SVG Icons */
function IconUpload({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
    </svg>
  );
}

function IconPlus({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function IconX({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

export function DirectionsTab() {
  const { terminals: refTerminals, locations, loading: refLoading, error: refError } = useReferenceData();
  const { setMessage } = useStatus();
  const [dirSubTab, setDirSubTab] = useState<'terminal' | 'batch'>('terminal');
  const [loading, setLoading] = useState(false);
  const [localTerminals, setLocalTerminals] = useState<{ id: string; name: string }[]>([]);
  const terminals = [...refTerminals, ...localTerminals];

  // Modal state for editing directions
  const [editingTerminal, setEditingTerminal] = useState<{ id: string; name: string } | null>(null);
  const [modalSteps, setModalSteps] = useState<DirectionStep[]>([]);
  const [modalSaving, setModalSaving] = useState(false);

  // Batch import state

  const [batchFile, setBatchFile] = useState<File | null>(null);
  const [batchPreview, setBatchPreview] = useState<ImportPreviewResponse | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] = useState<ImportCommitResponse | null>(null);

  // Create terminal state
  const [showCreateTerminal, setShowCreateTerminal] = useState(false);
  const [newTerminalName, setNewTerminalName] = useState('');
  const [newTerminalLat, setNewTerminalLat] = useState('');
  const [newTerminalLon, setNewTerminalLon] = useState('');
  const [newTerminalLocationId, setNewTerminalLocationId] = useState('');
  const [newTerminalFacilities, setNewTerminalFacilities] = useState('');
  const [newTerminalTransits, setNewTerminalTransits] = useState('');
  const [newTerminalKeywords, setNewTerminalKeywords] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [newTerminalSteps, setNewTerminalSteps] = useState<{ sequence: number; instruction: string; image: File | null; imagePreview: string | null }[]>([]);
  const [creatingTerminal, setCreatingTerminal] = useState(false);

  const [geocodeResult, setGeocodeResult] = useState('');
  const [pendingSql, setPendingSql] = useState<{ sql: string; title: string } | null>(null);

  // Geocode results modal state
  const [geocodeResults, setGeocodeResults] = useState<{ lat: string; lon: string; display_name: string; type: string }[]>([]);

  // Keywords editing state for existing terminals
  const [editingKeywords, setEditingKeywords] = useState<{ id: string; name: string } | null>(null);
  const [keywordsInput, setKeywordsInput] = useState('');
  const [savingKeywords, setSavingKeywords] = useState(false);

  // Nominatim geocoding: search terminal name → show results modal
  const geocodeTerminalName = async () => {
    if (!newTerminalName.trim()) { setMessage({ type: 'error', text: 'Enter terminal name first' }); return; }
    setGeocoding(true);
    setGeocodeResult('');
    try {
      const q = encodeURIComponent(newTerminalName);
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=8&countrycodes=ph`, {
        headers: { 'Accept': 'application/json' },
      });
      const data = await res.json() as { lat: string; lon: string; display_name: string; type: string }[];
      if (data.length === 0) {
        setGeocodeResult('No location found. Try a more specific name.');
        return;
      }
      // Show results modal for user to pick
      setGeocodeResults(data);
    } catch (err: any) {
      setGeocodeResult(err.message || 'Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  // Parse Google Maps URL or raw "lat, lon" to extract coordinates
  const parseGoogleMapsInput = (input: string): { lat: string; lon: string } | null => {
    // Try "lat, lon" format
    const coordMatch = input.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    if (coordMatch) return { lat: coordMatch[1], lon: coordMatch[2] };
    // Try Google Maps URL: /@lat,lon or /place/.../@lat,lon
    const urlMatch = input.match(/@(-?\d+\.?\d+),(-?\d+\.?\d+)/);
    if (urlMatch) return { lat: urlMatch[1], lon: urlMatch[2] };
    // Try !3d lat !4d lon pattern in Google Maps URLs
    const embedMatch = input.match(/!3d(-?\d+\.?\d+).*!4d(-?\d+\.?\d+)/);
    if (embedMatch) return { lat: embedMatch[1], lon: embedMatch[2] };
    return null;
  };

  const handlePasteCoords = (input: string) => {
    const parsed = parseGoogleMapsInput(input);
    if (parsed) {
      setNewTerminalLat(parsed.lat);
      setNewTerminalLon(parsed.lon);
      setGeocodeResult(`Coordinates extracted: ${parsed.lat}, ${parsed.lon}`);
    }
  };

  const selectGeocodeResult = (result: { lat: string; lon: string; display_name: string }) => {
    setNewTerminalLat(result.lat);
    setNewTerminalLon(result.lon);
    setGeocodeResult(result.display_name);
    setGeocodeResults([]);
  };

  // Open modal: load directions from DB and show modal
  const openDirectionsModal = async (terminal: { id: string; name: string }) => {
    setLoading(true);
    try {
      const res = await fetchDirections(terminal.id);
      setModalSteps(res.steps.map((s) => ({
        sequence: s.sequence,
        instruction: s.instruction,
        image: null,
        imagePreview: (typeof s.image === 'string' ? s.image : s.image?.uploaded_url) || null,
        saved: true,
      })));
      setEditingTerminal(terminal);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Save all steps from modal
  const handleModalSave = async (steps: DirectionStep[]) => {
    if (!editingTerminal) return;
    setModalSaving(true);
    const allSql: string[] = [];
    try {
      for (const step of steps) {
        if (!step.instruction.trim()) continue;
        const fd = new FormData();
        fd.append('terminal_id', editingTerminal.id);
        fd.append('sequence', String(step.sequence));
        fd.append('instruction', step.instruction);
        if (step.image) fd.append('image', step.image);
        const res = await upsertDirection(fd);
        allSql.push(res.sql);
      }
      setEditingTerminal(null);
      if (allSql.length > 0) {
        setPendingSql({ sql: allSql.join('\n\n'), title: `Directions — ${editingTerminal.name}` });
      }
      setMessage({ type: 'success', text: `Saved ${allSql.length} steps` });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setModalSaving(false);
    }
  };

  // Create terminal
  const handleCreateTerminal = async () => {
    if (!newTerminalName.trim()) { setMessage({ type: 'error', text: 'Terminal Name required' }); return; }
    if (!newTerminalLat || !newTerminalLon) { setMessage({ type: 'error', text: 'Latitude and Longitude are required' }); return; }
    setCreatingTerminal(true);
    try {
      const allSql: string[] = [];
      const res = await upsertTerminal({
        name: newTerminalName,
        lat: Number(newTerminalLat),
        lon: Number(newTerminalLon),
        location_id: newTerminalLocationId || undefined,
        facilities: newTerminalFacilities ? newTerminalFacilities.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        transits: newTerminalTransits ? newTerminalTransits.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        keywords: newTerminalKeywords ? newTerminalKeywords.split(',').map(s => s.toLowerCase().trim()).filter(Boolean) : undefined,
      });
      allSql.push(res.sql);
      const newTerminalId = res.terminal.id;
      setLocalTerminals([...localTerminals, { id: newTerminalId, name: res.terminal.name }]);
      setSelectedTerminal(newTerminalId);
      for (const step of newTerminalSteps) {
        if (step.instruction.trim()) {
          const fd = new FormData();
          fd.append('terminal_id', newTerminalId);
          fd.append('sequence', String(step.sequence));
          fd.append('instruction', step.instruction);
          if (step.image) fd.append('image', step.image);
          const stepRes = await upsertDirection(fd);
          allSql.push(stepRes.sql);
        }
      }
      await loadDirections();
      setShowCreateTerminal(false);
      setNewTerminalName('');
      setNewTerminalLat('');
      setNewTerminalLon('');
      setNewTerminalLocationId('');
      setNewTerminalFacilities('');
      setNewTerminalTransits('');
      setNewTerminalKeywords('');
      setGeocodeResult('');
      setNewTerminalSteps([]);
      setPendingSql({ sql: allSql.join('\n\n'), title: `Create Terminal: ${newTerminalName}` });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCreatingTerminal(false);
    }
  };

  const addNewTerminalStep = () => {
    const nextSeq = newTerminalSteps.length > 0 ? Math.max(...newTerminalSteps.map(s => s.sequence)) + 1 : 1;
    setNewTerminalSteps([...newTerminalSteps, { sequence: nextSeq, instruction: '', image: null, imagePreview: null }]);
  };

  const updateNewTerminalStep = (index: number, updates: Partial<{ sequence: number; instruction: string; image: File | null; imagePreview: string | null }>) => {
    setNewTerminalSteps(newTerminalSteps.map((s, i) => i === index ? { ...s, ...updates } : s));
  };

  const removeNewTerminalStep = (index: number) => {
    setNewTerminalSteps(newTerminalSteps.filter((_, i) => i !== index).map((s, i) => ({ ...s, sequence: i + 1 })));
  };

  const handleNewTerminalImageChange = (index: number, file: File) => {
    const reader = new FileReader();
    reader.onload = () => updateNewTerminalStep(index, { image: file, imagePreview: reader.result as string });
    reader.readAsDataURL(file);
  };

  const cancelCreateTerminal = () => {
    setShowCreateTerminal(false);
    setNewTerminalName('');
    setNewTerminalLat('');
    setNewTerminalLon('');
    setNewTerminalLocationId('');
    setNewTerminalFacilities('');
    setNewTerminalTransits('');
    setNewTerminalKeywords('');
    setGeocodeResult('');
    setNewTerminalSteps([]);
  };

  // Open keywords editor for existing terminal
  const openKeywordsEditor = (terminal: { id: string; name: string }) => {
    const existing = terminals.find(t => t.id === terminal.id);
    const kw = (existing as any)?.keywords as string[] | undefined;
    setKeywordsInput(kw?.join(', ') || '');
    setEditingKeywords(terminal);
  };

  const handleSaveKeywords = async () => {
    if (!editingKeywords) return;
    const keywords = keywordsInput.split(',').map(s => s.toLowerCase().trim()).filter(Boolean);
    if (keywords.length === 0) { setMessage({ type: 'error', text: 'Enter at least one keyword' }); return; }
    setSavingKeywords(true);
    try {
      const res = await updateTerminalKeywords({ terminal_id: editingKeywords.id, keywords });
      setPendingSql({ sql: res.sql, title: `Keywords — ${editingKeywords.name}` });
      setEditingKeywords(null);
      setMessage({ type: 'success', text: `Updated ${keywords.length} keywords` });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSavingKeywords(false);
    }
  };

  // Batch import
  const handleBatchPreview = async () => {
    if (!batchFile) { setMessage({ type: 'error', text: 'Directions workbook required' }); return; }
    setBatchLoading(true);
    setBatchResult(null);
    try {
      const fd = new FormData();
      fd.append('directionsWorkbook', batchFile);
      fd.append('idempotencyKey', crypto.randomUUID());
      const res = await previewImport(fd);
      setBatchPreview(res);
      setMessage({ type: 'success', text: 'Preview loaded' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchCommit = async () => {
    if (!batchPreview) return;
    setBatchLoading(true);
    try {
      const res = await commitImport({ importId: batchPreview.importId, mode: 'export_sql', conflictStrategy: 'upsert' });
      setBatchResult(res);
      if (res.sql) {
        setPendingSql({ sql: res.sql, title: 'Batch Import SQL' });
      } else {
        setMessage({ type: 'success', text: 'SQL exported' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBatchLoading(false);
    }
  };

  if (refLoading) return <div className="text-gray-500 p-8 text-center">Loading...</div>;
  if (refError) return <div className="text-red-500 p-8 text-center">Error: {refError}</div>;

  const dirTabCls = (tab: string) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
      dirSubTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className={cardCls}>
        <div className="flex border-b border-gray-200">
          <button onClick={() => setDirSubTab('terminal')} className={dirTabCls('terminal')}>Terminal Directions</button>
          <button onClick={() => setDirSubTab('batch')} className={dirTabCls('batch')}>Batch Import</button>
        </div>
      </div>

      {/* === TERMINAL DIRECTIONS === */}
      {dirSubTab === 'terminal' && <>
      {/* Terminal Selection */}
      <div className={cardCls}>
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setShowCreateTerminal(true)} className={btnSuccess}>
              <span className="flex items-center gap-1.5"><IconPlus /> New Terminal</span>
            </button>
            {loading && <span className="text-xs text-gray-400 animate-pulse">Loading...</span>}
          </div>

          {/* Create Terminal Form */}
          {showCreateTerminal && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
              <h4 className="text-sm font-semibold text-gray-800">Create New Terminal</h4>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Terminal Name *</label>
                <div className="flex gap-2">
                  <input type="text" value={newTerminalName} onChange={(e) => setNewTerminalName(e.target.value)}
                    placeholder="e.g., Cubao Central Terminal" className={inputCls} />
                  <button onClick={geocodeTerminalName} disabled={geocoding || !newTerminalName.trim()}
                    title="Search coordinates from name (Nominatim)"
                    className="px-3 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50 flex-shrink-0 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                    {geocoding ? 'Searching...' : 'Locate'}
                  </button>
                </div>
                {geocodeResult && (
                  <p className={`text-xs mt-1 ${newTerminalLat ? 'text-green-600' : 'text-red-500'}`}>{geocodeResult}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Google Maps URL or Coordinates</label>
                <input type="text"
                  placeholder="Paste Google Maps URL or lat, lon (e.g. 14.6266, 121.0474)"
                  className={inputCls}
                  onChange={(e) => handlePasteCoords(e.target.value)}
                  onPaste={(e) => { setTimeout(() => handlePasteCoords((e.target as HTMLInputElement).value), 0); }}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Auto-extracts coordinates from Google Maps links</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Latitude *</label>
                  <input type="number" step="any" value={newTerminalLat} onChange={(e) => setNewTerminalLat(e.target.value)}
                    placeholder="14.5995" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Longitude *</label>
                  <input type="number" step="any" value={newTerminalLon} onChange={(e) => setNewTerminalLon(e.target.value)}
                    placeholder="120.9842" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                <select value={newTerminalLocationId} onChange={(e) => setNewTerminalLocationId(e.target.value)} className={inputCls + ' bg-white'}>
                  <option value="">— None —</option>
                  {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Facilities</label>
                  <input value={newTerminalFacilities} onChange={(e) => setNewTerminalFacilities(e.target.value)}
                    placeholder="wifi, restroom, parking" className={inputCls} />
                  <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Transits</label>
                  <input value={newTerminalTransits} onChange={(e) => setNewTerminalTransits(e.target.value)}
                    placeholder="jeepney, taxi, grab" className={inputCls} />
                  <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Keywords</label>
                <input value={newTerminalKeywords} onChange={(e) => setNewTerminalKeywords(e.target.value)}
                  placeholder="pitx, baguio, baguio city, pitx baguio" className={inputCls} />
                <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated, auto-lowercased</p>
              </div>

              {/* Steps for new terminal */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Direction Steps</span>
                  <button onClick={addNewTerminalStep} className="text-sm text-blue-600 hover:text-blue-800 font-medium cursor-pointer flex items-center gap-1">
                    <IconPlus className="w-3.5 h-3.5" /> Add Step
                  </button>
                </div>
                {newTerminalSteps.map((step, index) => (
                  <div key={index} className="flex gap-3 items-stretch p-3 bg-white rounded-md border border-gray-200">
                    <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-blue-100 text-blue-700 rounded-full font-semibold text-xs self-center">
                      {step.sequence}
                    </div>
                    <div className="flex-shrink-0">
                      {step.imagePreview ? (
                        <div className="relative">
                          <img src={step.imagePreview} alt="" className="w-40 h-40 object-cover rounded-md border border-gray-200" />
                          <button onClick={() => updateNewTerminalStep(index, { image: null, imagePreview: null })}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center cursor-pointer">
                            <IconX className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                          <IconUpload className="w-4 h-4 text-gray-400" />
                          <span className="text-[10px] text-gray-400 mt-0.5">Image</span>
                          <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && handleNewTerminalImageChange(index, e.target.files[0])} className="hidden" />
                        </label>
                      )}
                    </div>
                    <textarea value={step.instruction} onChange={(e) => updateNewTerminalStep(index, { instruction: e.target.value })}
                      placeholder="Enter instruction..." className={`${inputCls} resize-none h-20`} />
                    <button onClick={() => removeNewTerminalStep(index)} className="text-gray-400 hover:text-red-500 self-center cursor-pointer transition-colors">
                      <IconX />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2 border-t border-gray-200">
                <button onClick={handleCreateTerminal} disabled={creatingTerminal || !newTerminalName} className={btnSuccess}>
                  {creatingTerminal ? 'Creating...' : `Create Terminal${newTerminalSteps.length > 0 ? ` (${newTerminalSteps.length} steps)` : ''}`}
                </button>
                <button onClick={cancelCreateTerminal} className={btnSecondary}>Cancel</button>
              </div>
            </div>
          )}

          {/* Terminals Table */}
          {terminals.length === 0 && !showCreateTerminal ? (
            <p className="text-sm text-gray-400 py-4 text-center">No terminals yet. Click "New Terminal" to create one.</p>
          ) : terminals.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden mt-3 shadow-sm">
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px]">Terminal</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[150px]">Location</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">Keywords</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[100px]">Facilities</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[100px]">Transits</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-40 sticky right-0 bg-gray-50">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {terminals.map((t) => {
                      const loc = locations.find(l => l.id === (t as any).location_id);
                      return (
                        <tr key={t.id} className="hover:bg-blue-50/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{t.name}</div>
                            {t.lat && t.lon && <div className="text-[11px] text-gray-400 mt-0.5">📍 {t.lat.toFixed(4)}, {t.lon.toFixed(4)}</div>}
                          </td>
                          <td className="px-3 py-3 text-gray-600">{loc?.name || <span className="text-gray-300">—</span>}</td>
                          <td className="px-3 py-3">
                            {(t as any).keywords?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {((t as any).keywords as string[]).slice(0, 3).map((kw, i) => (
                                  <span key={i} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-medium">{kw}</span>
                                ))}
                                {((t as any).keywords as string[]).length > 3 && (
                                  <span className="text-[11px] text-gray-400">+{((t as any).keywords as string[]).length - 3} more</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {(t as any).facilities?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {((t as any).facilities as string[]).slice(0, 2).map((f, i) => (
                                  <span key={i} className="inline-block px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[10px]">{f}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {(t as any).transits?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {((t as any).transits as string[]).slice(0, 2).map((tr, i) => (
                                  <span key={i} className="inline-block px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px]">{tr}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right sticky right-0 bg-white">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openKeywordsEditor(t)}
                                disabled={loading}
                                className="p-1.5 rounded text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer disabled:opacity-50"
                                title="Edit keywords"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => openDirectionsModal(t)}
                                disabled={loading}
                                className="p-1.5 rounded text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer disabled:opacity-50"
                                title="Edit directions"
                              >
                                📝
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Direction Steps Modal */}
      {editingTerminal && (
        <DirectionStepsModal
          terminalName={editingTerminal.name}
          initialSteps={modalSteps}
          onSave={handleModalSave}
          onClose={() => setEditingTerminal(null)}
          saving={modalSaving}
        />
      )}

      {/* Keywords Editor Modal */}
      {editingKeywords && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-5 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Keywords — {editingKeywords.name}</h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Keywords</label>
              <textarea
                value={keywordsInput}
                onChange={(e) => setKeywordsInput(e.target.value)}
                placeholder="pitx, baguio, baguio city, pitx baguio, benguet"
                className={`${inputCls} resize-none h-24`}
              />
              <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated, auto-lowercased</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingKeywords(null)} className={btnSecondary}>Cancel</button>
              <button onClick={handleSaveKeywords} disabled={savingKeywords} className={btnPrimary}>
                {savingKeywords ? 'Saving...' : 'Save Keywords'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Geocode Results Picker Modal */}
      {geocodeResults.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Select Location</h3>
                <p className="text-xs text-gray-500 mt-0.5">{geocodeResults.length} results for "{newTerminalName}"</p>
              </div>
              <button onClick={() => setGeocodeResults([])} className="text-gray-400 hover:text-gray-600 cursor-pointer"><IconX className="w-4 h-4" /></button>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
              {geocodeResults.map((r, i) => (
                <button
                  key={i}
                  onClick={() => selectGeocodeResult(r)}
                  className="w-full px-5 py-3 text-left hover:bg-blue-50 transition-colors cursor-pointer flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-700 rounded-full text-xs font-semibold mt-0.5">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800 leading-snug">{r.display_name}</p>
                    <p className="text-[11px] text-gray-400 mt-1 font-mono">{r.lat}, {r.lon} · {r.type}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      </>}

      {/* Batch Import Section */}
      {dirSubTab === 'batch' &&
      <div className={cardCls}>
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">Batch Import (XLSX)</h3>
          <p className="text-xs text-gray-500 mt-0.5">Upload directions workbook to import terminal directions in bulk</p>
        </div>
        <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Directions Workbook *</label>
              <input type="file" accept=".xlsx,.xls" onChange={(e) => setBatchFile(e.target.files?.[0] || null)} className="text-sm" />
              {batchFile && <span className="text-xs text-gray-500 ml-2">{batchFile.name}</span>}
            </div>
            <button onClick={handleBatchPreview} disabled={batchLoading || !batchFile} className={btnPrimary}>
              {batchLoading ? 'Processing...' : 'Preview Import'}
            </button>
            {batchPreview && (
              <div className="space-y-4">
                {/* Summary bar */}
                <div className="bg-blue-50 rounded-md p-3 border border-blue-200 flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-xs text-blue-500 uppercase tracking-wider block">Terminals</span>
                    <span className="text-lg font-semibold text-blue-800">{batchPreview.terminals_summary?.length ?? 0}</span>
                  </div>
                  <div>
                    <span className="text-xs text-blue-500 uppercase tracking-wider block">Steps</span>
                    <span className="text-lg font-semibold text-blue-800">{batchPreview.counts.direction_steps}</span>
                  </div>
                  <div>
                    <span className="text-xs text-blue-500 uppercase tracking-wider block">With Images</span>
                    <span className="text-lg font-semibold text-blue-800">{batchPreview.counts.directions_with_images}</span>
                  </div>
                </div>

                {/* Terminals breakdown */}
                {batchPreview.terminals_summary && batchPreview.terminals_summary.length > 0 && (
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Terminal (Sheet Name)</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">Steps</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">Images</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Instructions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {batchPreview.terminals_summary.map((t) => (
                          <tr key={t.terminal_id} className="hover:bg-gray-50/50">
                            <td className="px-3 py-2 font-medium text-gray-800">{t.name}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{t.steps}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{t.with_images}</td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                              {t.instructions.map((ins, i) => (
                                <div key={i} className="truncate max-w-md">{ins}</div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Warnings & Errors */}
                {batchPreview.warnings.length > 0 && (
                  <details className="text-amber-600 text-sm">
                    <summary className="font-medium cursor-pointer">{batchPreview.warnings.length} warnings</summary>
                    <ul className="mt-1 text-xs space-y-0.5 pl-4 list-disc">{batchPreview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                  </details>
                )}
                {batchPreview.errors.length > 0 && (
                  <div className="bg-red-50 rounded-md p-3 border border-red-200">
                    <p className="text-sm text-red-600 font-medium">{batchPreview.errors.length} errors</p>
                    <ul className="mt-1 text-xs text-red-700 space-y-0.5 pl-4 list-disc">{batchPreview.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button onClick={handleBatchCommit} disabled={batchLoading || batchPreview.errors.length > 0}
                    className={btnSuccess}>
                    {batchLoading ? 'Processing...' : 'Commit'}
                  </button>
                </div>
              </div>
            )}
            {batchResult && (
              <div className="bg-green-50 rounded-md p-3 border border-green-200">
                <p className="text-sm font-medium text-green-800">{batchResult.status === 'applied' ? 'Applied to DB' : 'SQL Exported'}</p>
                {batchResult.sql_artifact_url && <a href={batchResult.sql_artifact_url} download className="text-sm text-blue-600 hover:underline">Download SQL</a>}
              </div>
            )}
        </div>
      </div>}

      {/* SQL Action Modal */}
      {pendingSql && (
        <SqlActionModal
          sql={pendingSql.sql}
          title={pendingSql.title}
          onClose={() => setPendingSql(null)}
          onApplied={() => setMessage({ type: 'success', text: 'Applied to DB' })}
        />
      )}
    </div>
  );
}
